import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { useEffect, lazy, Suspense } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

// Components
import Navigation from './components/Navigation'
import Footer from './components/Footer'
import ErrorBoundary from './components/ErrorBoundary'
import { LanguageProvider } from './context/LanguageContext'

// Pages — only the homepage loads eagerly; every other page is code-split
// into its own chunk and fetched on first visit, keeping the initial
// download small.
import HomePage from './pages/HomePage'
const PackagesPage = lazy(() => import('./pages/PackagesPage'))
const PlanDetailsPage = lazy(() => import('./pages/PlanDetailsPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const SupportPage = lazy(() => import('./pages/SupportPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const LegalPage = lazy(() => import('./pages/LegalPage'))
const SignInPage = lazy(() => import('./pages/SignInPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const CheckServicesPage = lazy(() => import('./pages/CheckServicesPage'))
const OrderPage = lazy(() => import('./pages/OrderPage'))
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'))
const SwitchPage = lazy(() => import('./pages/SwitchPage'))
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'))
const HostPage = lazy(() => import('./pages/HostPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const SpeedTestPage = lazy(() => import('./pages/SpeedTestPage'))
const NetworkUsagePage = lazy(() => import('./pages/NetworkUsagePage'))

// Shown while a lazy page chunk downloads (typically only a moment)
const PageLoader = () => (
  <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <CircularProgress />
  </Box>
)

// Create a custom theme for Direct Connect Wales with authentic Welsh flag colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#00B140', // Welsh flag green (Y Ddraig Goch green)
      light: '#33C466',
      dark: '#008F33',
    },
    secondary: {
      main: '#DD2033', // Welsh flag red (Dragon red - Y Ddraig Goch)
      light: '#E74C5C',
      dark: '#B81A29',
    },
    background: {
      default: '#FFFFFF', // White from Welsh flag
      paper: '#ffffff',
    },
    text: {
      primary: '#1E2227',
      secondary: '#4A5568',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }: any) => ({
          transition: 'transform 150ms ease, box-shadow 150ms ease',
          boxShadow: ownerState.variant === 'contained' ? '0 6px 16px rgba(0,0,0,0.20)' : 'none',
          '&:hover': {
            transform: 'translateY(-2px) scale(1.02)',
            boxShadow:
              ownerState.variant === 'contained'
                ? '0 10px 24px rgba(0,0,0,0.28)'
                : '0 8px 20px rgba(0,0,0,0.16)',
          },
          '&:active': {
            transform: 'translateY(0) scale(0.99)'
          }
        }),
      },
    },
  },
})

// Create a client for React Query
const queryClient = new QueryClient()

// Component to track page views with Matomo
function MatomoPageTracker() {
  const location = useLocation()
  
  useEffect(() => {
    // Track page view on route change
    const trackPageView = () => {
      const _paq = (window as any)._paq
      
      if (_paq) {
        const pageUrl = location.pathname + location.search
        const pageTitle = document.title
        
        _paq.push(['setCustomUrl', pageUrl])
        _paq.push(['setDocumentTitle', pageTitle])
        _paq.push(['deleteCustomVariables', 'page'])
        _paq.push(['trackPageView'])
      }
    }
    
    // Small delay to ensure DOM is updated
    setTimeout(trackPageView, 300)
  }, [location.pathname, location.search])
  
  return null
}

function App() {
  return (
    
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LanguageProvider>
              <Router>
              <MatomoPageTracker />
              <Suspense fallback={<PageLoader />}>
              <Routes>
            {/* Dashboard routes - no Navigation/Footer */}
            <Route path="/dashboard/*" element={<DashboardPage />} />
            
            {/* Sign In route - no Navigation/Footer */}
            <Route path="/signin" element={<SignInPage />} />
            
            {/* Sign Up route - no Navigation/Footer */}
            <Route path="/signup" element={<SignUpPage />} />
            
            {/* Public routes - with Navigation/Footer */}
            <Route path="/*" element={
              <>
                <Navigation />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/get-started" element={<OnboardingPage />} />
                  <Route path="/packages" element={<PackagesPage />} />
                  <Route path="/packages/:id" element={<PlanDetailsPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/legal" element={<LegalPage />} />
                  <Route path="/check-services" element={<CheckServicesPage />} />
                  <Route path="/switch" element={<SwitchPage />} />
                  <Route path="/order" element={<OrderPage />} />
                  <Route path="/order/success" element={<OrderSuccessPage />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  <Route path="/host" element={<HostPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/speedtest" element={<SpeedTestPage />} />
                  <Route path="/network-usage" element={<NetworkUsagePage />} />
                </Routes>
                <Footer />
              </>
            } />
            </Routes>
              </Suspense>
              </Router>
            </LanguageProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

export default App